import React, { useState, useEffect, useCallback } from 'react';
import { Reorder } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { speciesAPI } from '../../../services/api';
import { showSuccessToast, showErrorToast } from '../../../utils/notificationUtils';
import SpeciesForm from './SpeciesForm';
import './BreedManager.css'; // reuse same styles

const SpeciesManager = ({ initialView = 'list' }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [speciesList, setSpeciesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // View states: 'list' or 'form'
  const [view, setView] = useState('list');
  const [editingSpecies, setEditingSpecies] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Form State
  const emptyForm = {
    name: '',
    name_en: '',
    name_ar: '',
    display_name: '',
    description: '',
    description_en: '',
    description_ar: '',
    image_url: '',
    is_active: true,
    sort_order: 0,
    background_color: '#ffffff',
    image: null,
    remove_image: false,
    existing_image_url: '',
  };

  const [formData, setFormData] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await speciesAPI.getSpecies({ all: true, search: searchTerm });
      const data = res.data?.data || (Array.isArray(res.data) ? res.data : []);
      // Keep Data Entry species out of the CMS encyclopedia view
      setSpeciesList(data.filter((species) => species.show_in_encyclopedia !== false));
    } catch (error) {
      console.error('Error fetching species:', error);
      showErrorToast(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [searchTerm, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenForm = (species = null) => {
    if (species) {
      setEditingSpecies(species);
      setFormData({
        name:              species.name || '',
        name_en:           species.name_en || '',
        name_ar:           species.name_ar || '',
        display_name:      species.display_name || '',
        description:       species.description || '',
        description_en:    species.description_en || '',
        description_ar:    species.description_ar || '',
        image_url:         species.image_url || '',
        is_active:         species.is_active,
        sort_order:        species.sort_order || 0,
        background_color:  species.background_color || '#ffffff',
        image:             null,
        remove_image:      false,
        existing_image_url: species.image_url || '',
      });
    } else {
      setEditingSpecies(null);
      setFormData(emptyForm);
    }
    setView('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseForm = () => {
    setView('list');
    setEditingSpecies(null);
  };

  // Handle external view changes (from sidebar)
  useEffect(() => {
    if (initialView === 'form') {
      handleOpenForm();
    } else {
      handleCloseForm();
    }
  }, [initialView]);

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingSpecies) {
        await speciesAPI.updateSpecies(editingSpecies.id, formData);
        showSuccessToast(t('updateSuccess'));
      } else {
        await speciesAPI.createSpecies(formData);
        showSuccessToast(t('createSuccess'));
      }
      handleCloseForm();
      fetchData();
    } catch (error) {
      console.error('Error saving species:', error);
      showErrorToast(error.response?.data?.message || t('errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (species) => {
    if (!species?.id) return;
    setDeleteTarget(species);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await speciesAPI.deleteSpecies(deleteTarget.id, { targetName: deleteTarget.name_en || deleteTarget.name || deleteTarget.name_ar });
      showSuccessToast(t('deleteSuccess'));
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchData();
    } catch {
      showErrorToast(t('errorOccurred'));
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleReorder = async (newOrder) => {
    setSpeciesList(newOrder);
    try {
      await speciesAPI.reorderSpecies(newOrder.map(s => s.id));
      showSuccessToast(t('adminDashboard.cms.order_updated_successfully', 'Order updated successfully'));
    } catch (error) {
      console.error('Error reordering species:', error);
      showErrorToast(t('errorOccurred'));
      fetchData();
    }
  };

  if (view === 'form') {
    return (
      <SpeciesForm
        formData={formData}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        handleCancel={handleCloseForm}
        submitting={submitting}
        isEditing={!!editingSpecies}
      />
    );
  }

  return (
    <div className={`breed-manager-container ${isRTL ? 'rtl' : ''}`}>
      <div className="breed-manager-header">
        <h2 className="admin-dashboard-section-title">{t('adminDashboard.menu.cms_species', 'Species')}</h2>
        {/* Add Species button hidden as requested */}
        {/* <button className="add-breed-btn" onClick={() => handleOpenForm()}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          {t('adminDashboard.cms.add_species', 'Add Species')}
        </button> */}
      </div>

      <div className="breed-manager-filters">
        <div className="search-box">
          <div className="icon-input-wrapper">
            <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={t('adminDashboard.cms.search_species', 'Search species...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="search-clear-btn" onClick={() => setSearchTerm('')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="breed-list-container">
        {loading ? (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        ) : speciesList.length > 0 ? (
          <div className="breed-table-wrapper">
            <table className="breed-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>{t('adminDashboard.cms.image', 'Image')}</th>
                  <th>{t('adminDashboard.cms.name', 'Name')}</th>
                  <th>{t('adminDashboard.cms.breeds_count', 'Breeds')}</th>
                  <th>{t('adminDashboard.cms.status', 'Status')}</th>
                  <th style={{ textAlign: 'center' }}>{t('adminDashboard.cms.actions', 'Actions')}</th>
                </tr>
              </thead>
              <Reorder.Group as="tbody" axis="y" values={speciesList} onReorder={handleReorder}>
                {speciesList.map(species => (
                  <Reorder.Item as="tr" key={species.id} value={species} className="draggable-row">
                    <td>
                      <div className="drag-handle">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="9" cy="5" r="1"></circle>
                          <circle cx="9" cy="12" r="1"></circle>
                          <circle cx="9" cy="19" r="1"></circle>
                          <circle cx="15" cy="5" r="1"></circle>
                          <circle cx="15" cy="12" r="1"></circle>
                          <circle cx="15" cy="19" r="1"></circle>
                        </svg>
                      </div>
                    </td>
                    <td>
                      <div className="breed-image-preview">
                        {species.image_url ? (
                          <img src={species.image_url} alt={species.name} />
                        ) : (
                          <div className="no-image-placeholder">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <circle cx="8.5" cy="8.5" r="1.5"></circle>
                              <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="breed-name-cell">
                        <span className="primary-name">{isRTL ? (species.name_ar || species.name) : (species.name_en || species.name)}</span>
                        <span className="secondary-name">{isRTL ? (species.name_en || species.display_name) : (species.name_ar || species.display_name)}</span>
                      </div>
                    </td>
                    <td>{species.breed_types_count ?? '—'}</td>
                    <td>
                      <span className={`status-badge ${species.is_active ? 'active' : 'inactive'}`}>
                        {species.is_active ? t('adminDashboard.cms.active', 'Active') : t('adminDashboard.cms.inactive', 'Inactive')}
                      </span>
                    </td>
                    <td align="center">
                      <div className="action-buttons">
                        <button className="edit-btn" onClick={() => handleOpenForm(species)} title={t('edit')}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button className="delete-btn" onClick={() => handleDelete(species)} title={t('delete')}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </Reorder.Item>
                ))}
              </Reorder.Group>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <ellipse cx="12" cy="12" rx="10" ry="6"></ellipse>
                <path d="M2 12c0 4 4.5 7 10 7s10-3 10-7"></path>
                <path d="M12 5v14"></path>
              </svg>
            </div>
            <h3>{t('adminDashboard.cms.no_species_found', 'No species found')}</h3>
            <p>{t('adminDashboard.cms.try_adjusting_filters', 'Try adjusting your filters or search criteria to find what you\'re looking for.')}</p>
            <button className="clear-filters-btn" onClick={() => setSearchTerm('')}>
              {t('adminDashboard.cms.clear_all_filters', 'Clear All Filters')}
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="breed-delete-overlay" onClick={cancelDelete}>
          <div className="breed-delete-modal" onClick={(e) => e.stopPropagation()} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="breed-delete-modal-header">
              <div className="breed-delete-modal-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </div>
              <div>
                <h3>{t('adminDashboard.cms.confirmDeleteSpeciesTitle', 'Delete Species')}</h3>
                <p>{t('adminDashboard.cms.confirmDeleteSpeciesMessage', 'Are you sure you want to delete this species? This action cannot be undone.')}</p>
              </div>
            </div>
            <div className="breed-delete-modal-actions">
              <button className="breed-delete-cancel" onClick={cancelDelete}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button className="breed-delete-confirm" onClick={confirmDelete}>
                {t('common.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpeciesManager;
